import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import CustomerDetail from './CustomerDetail';
import { SEGMENTS, type CustomerRow, apiError } from './lib';

const BLANK = { name: '', phone: '', email: '', notes: '' };

export default function CustomersTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  // Lifetime spend is aggregated across every branch a customer has visited, so
  // it is stated in the tenant's base currency rather than the active branch's.
  const { format: formatMoney } = useCurrency(null);
  const canCreate = can('customers.create');

  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: CustomerRow[] }>('/customers', {
        params: { limit: 100, search: search || undefined, segment: segment || undefined },
      })
      .then(({ data }) => {
        const list = data.data ?? [];
        setRows(list);
        setSelectedId((current) =>
          current && list.some((c) => c.id === current) ? current : (list[0]?.id ?? null),
        );
      })
      .catch((e) => setError(apiError(e, t('crm.customers.loadFailed'))))
      .finally(() => setLoading(false));
  }, [search, segment, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post<CustomerRow>('/customers', {
        name: form.name,
        phone: form.phone || undefined,
        email: form.email || undefined,
        notes: form.notes || undefined,
      });
      toast.success(t('crm.customers.created'));
      setFormOpen(false);
      setForm(BLANK);
      setSelectedId(data.id);
      load();
    } catch (e) {
      // Deduplicated by phone — a clash is a 409 with a readable message.
      toast.error(apiError(e, t('crm.customers.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[22rem_1fr]">
      <Card className="h-fit">
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle>{t('crm.customers.title')}</CardTitle>
          {canCreate && (
            <Button size="sm" onClick={() => setFormOpen(true)}>
              {t('common.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder={t('crm.customers.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            aria-label={t('crm.customers.segment')}
          >
            <option value="">{t('crm.customers.allSegments')}</option>
            {SEGMENTS.map((value) => (
              <option key={value} value={value}>
                {t(`crm.segment.${value}`)}
              </option>
            ))}
          </Select>
          <p className="text-xs text-ink-muted">{t('crm.customers.segmentNote')}</p>

          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('crm.customers.empty')} hint={t('crm.customers.emptyHint')} />
          ) : (
            <ul className="scrollbar-thin max-h-[32rem] divide-y overflow-y-auto rounded-lg border">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    onClick={() => setSelectedId(row.id)}
                    className={`w-full p-3 text-start text-sm hover:bg-hover ${
                      row.id === selectedId ? 'bg-accent-soft' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {row.name ?? t('crm.customers.unnamed')}
                      </span>
                      <Badge tone={row.segment === 'VIP' ? 'brand' : 'neutral'}>
                        {t(`crm.segment.${row.segment}`)}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-ink-muted">
                      <span>{row.phone ?? '—'}</span>
                      <span>
                        {t('crm.customers.visitsShort', { count: row.visits })} ·{' '}
                        {formatMoney(row.totalSpend)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div>
        {selectedId ? (
          <CustomerDetail customerId={selectedId} onChanged={load} />
        ) : (
          <Card>
            <CardContent className="p-5">
              <EmptyState title={t('crm.customers.nothingSelected')} />
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('crm.customers.newCustomer')}
        description={t('crm.customers.dedupeHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving || !form.name.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('crm.customers.name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('crm.customers.phone')}>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label={t('crm.customers.email')}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label={t('crm.customers.notes')} hint={t('crm.customers.notesHint')}>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
